<template>
  <div :class="$style.container">
    <SearchFilters
      :searchLocation="searchLocation"
      :propertyType="propertyType"
      :allowClear="isPlatformHg()"
      @onPropertyTypeChange="handlePropertyTypeChange"
      @onSelectedLocationChange="handleSelectedLocationChange"
    />
  </div>
</template>

<script setup lang="ts">
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import SearchFilters from '@/components/search-bar/SearchFilters.vue';
import useRouterLocationPush from '@/composables/useRouterLocationPush';
import { getCulture } from '@/helpers/localizeLanguage';
import { isPlatformHg } from '@/helpers/platform';
import { PropertyType } from '@/models/iazi/common/enums';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { trackLocationSearch } from '@/tracking';
import { EventSource } from '@/types';

const { locale } = useI18n();
const { pushLocation } = useRouterLocationPush();

const geoStore = useGeoStore();
const { searchLocation, getIaziLevelByGeoType } = storeToRefs(geoStore);
const mapStore = useMapStore();
const { propertyType } = storeToRefs(mapStore);

const handlePropertyTypeChange = (value: PropertyType): void => {
  mapStore.setPropertyType(value);
  mapStore.fetchPriceDistributions(value, getCulture(locale.value as HgSupportedLanguage));
};
const handleSelectedLocationChange = async (location: GeoLocation | null): Promise<void> => {
  mapStore.setLoading(true);
  await geoStore.setSearchLocation(location);
  mapStore.setCurrentLevel(getIaziLevelByGeoType.value);
  await pushLocation(EventSource.Search);
  trackLocationSearch(searchLocation.value!.id, propertyType.value);
};
</script>

<style module lang="scss">
@use '@/styles/variables' as *;

.container {
  display: none;

  @include grid-media-query(sm) {
    display: block;
  }
}
</style>
